import { d as addItemById, a as contexts } from "./cart-cce84c00.js";
import { E as EVENTS } from "./events-58bc9098.js";

/**
 * A product card placed inline within blog article content.
 *
 * Everything on the card is rendered by Liquid. This only adds the optional add to cart
 * button behaviour, which reuses the theme's cart module so the cart drawer opens in the
 * same way it does from a product listing.
 */
class InlineProductCard extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      addToCartWrapper: "[data-inline-product-add-to-cart]",
      addToCartButton: "[data-inline-product-add-to-cart] button[data-variant-id]",
      error: "[data-inline-product-error]",
    };
  }

  connectedCallback() {
    // Guard against the element being moved around the DOM and reconnected.
    this.controller?.abort();

    this.addToCartWrapper = this.querySelector(this.selectors.addToCartWrapper);
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);

    if (!this.addToCartWrapper || !this.addToCartButton) return;

    this.error = this.querySelector(this.selectors.error);
    this.controller = new AbortController();

    this.addToCartButton.addEventListener(
      "click",
      this.handleAddToCart.bind(this),
      { signal: this.controller.signal },
    );

    this.addEventListener(
      EVENTS.QUICK_ADD_SUCCESS,
      this.handleAddToCartSuccess.bind(this),
      { signal: this.controller.signal },
    );

    this.addEventListener(
      EVENTS.QUICK_ADD_ERROR,
      this.handleAddToCartError.bind(this),
      { signal: this.controller.signal },
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  handleAddToCart() {
    this.hideError();

    this.addToCartWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_STARTED),
    );

    addItemById(
      this.addToCartButton.dataset.variantId,
      1,
      contexts.productItem,
      this,
    );
  }

  handleAddToCartSuccess() {
    this.addToCartWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED),
    );
  }

  handleAddToCartError(event) {
    this.addToCartWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED),
    );

    if (!this.error) return;

    this.error.textContent = event.detail?.errorMessage || "";
    this.error.hidden = !this.error.textContent;
  }

  hideError() {
    if (!this.error) return;

    this.error.textContent = "";
    this.error.hidden = true;
  }
}

/**
 * Stands in for a card that Liquid couldn't render, because Shopify only allows 20 products
 * per page through 'all_products'. It fetches the card markup from the product's own URL via
 * the Section Rendering API and swaps itself out for it.
 *
 * The placeholder always contains a plain link to the product, so if the fetch fails - or
 * JavaScript never runs - the reader still gets somewhere to go.
 */
class InlineProductCardPlaceholder extends HTMLElement {
  connectedCallback() {
    if (this.hydrating) return;
    this.hydrating = true;

    if (!("IntersectionObserver" in window)) {
      this.hydrate();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.hydrate();
      },
      { rootMargin: "600px 0px" },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async hydrate() {
    const url = this.dataset.url;

    if (!url) return;

    try {
      const response = await fetch(`${url}?section_id=inline-product-card`);

      if (!response.ok) return;

      const markup = await response.text();
      const card = new DOMParser()
        .parseFromString(markup, "text/html")
        .querySelector("inline-product-card");

      if (!card) return;

      this.applyOptions(card);
      this.replaceWith(card);
    } catch (error) {
      // Leave the placeholder's link in place - it's still a working route to the product.
    }
  }

  /**
   * Re-applies the options from the original marker. The section is rendered outside of a
   * template, so it can't know about them itself.
   *
   * @param {HTMLElement} card - The freshly fetched card.
   */
  applyOptions(card) {
    const { layout, eyebrow, badge, note, description } = this.dataset;

    if (layout) {
      card.classList.remove(
        "inline-product-card--horizontal",
        "inline-product-card--vertical",
      );
      card.classList.add(`inline-product-card--${layout}`);
    }

    this.fillSlot(card, "[data-inline-product-eyebrow]", eyebrow);
    this.fillSlot(card, "[data-inline-product-note]", note);
    this.fillSlot(card, "[data-inline-product-description]", description);

    if (badge) {
      const badges = card.querySelector("[data-inline-product-badges]");

      if (badges) {
        const element = document.createElement("span");

        element.className =
          "badge inline-product-card__badge inline-product-card__badge--custom";
        element.textContent = badge;

        badges.replaceChildren(element);
        badges.hidden = false;
      }
    }
  }

  /**
   * @param {HTMLElement} card - The freshly fetched card.
   * @param {string} selector - The slot to fill.
   * @param {string} [value] - The text to write into it.
   */
  fillSlot(card, selector, value) {
    if (!value) return;

    const slot = card.querySelector(selector);

    if (!slot) return;

    slot.textContent = value;
    slot.hidden = false;
  }
}

if (!customElements.get("inline-product-card")) {
  customElements.define("inline-product-card", InlineProductCard);
}

if (!customElements.get("inline-product-card-placeholder")) {
  customElements.define(
    "inline-product-card-placeholder",
    InlineProductCardPlaceholder,
  );
}